import React, { useState } from 'react';
import { SearchIcon, DownloadIcon, UploadIcon, InfoIcon } from 'lucide-react';
export const CareerGenomeMatrix: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTraits, setSelectedTraits] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ascending' | 'descending';
  } | null>(null);
  // Sample data
  const traits = ['Communication', 'Creativity', 'Analytical Thinking', 'Leadership', 'Technical Aptitude'];
  const careers = [{
    id: 1,
    title: 'UX Designer',
    category: 'Design',
    traitScores: {
      Communication: 85,
      Creativity: 95,
      'Analytical Thinking': 80,
      Leadership: 60,
      'Technical Aptitude': 75
    }
  }, {
    id: 2,
    title: 'Data Scientist',
    category: 'Technology',
    traitScores: {
      Communication: 70,
      Creativity: 75,
      'Analytical Thinking': 95,
      Leadership: 65,
      'Technical Aptitude': 90
    }
  }, {
    id: 3,
    title: 'Product Manager',
    category: 'Business',
    traitScores: {
      Communication: 90,
      Creativity: 80,
      'Analytical Thinking': 85,
      Leadership: 85,
      'Technical Aptitude': 70
    }
  }, {
    id: 4,
    title: 'Marketing Specialist',
    category: 'Marketing',
    traitScores: {
      Communication: 95,
      Creativity: 90,
      'Analytical Thinking': 75,
      Leadership: 70,
      'Technical Aptitude': 60
    }
  }, {
    id: 5,
    title: 'Software Engineer',
    category: 'Technology',
    traitScores: {
      Communication: 65,
      Creativity: 70,
      'Analytical Thinking': 90,
      Leadership: 60,
      'Technical Aptitude': 95
    }
  }];
  // Filter and sort careers
  const filteredCareers = careers.filter(career => career.title.toLowerCase().includes(searchTerm.toLowerCase()));
  const sortedCareers = sortConfig !== null ? [...filteredCareers].sort((a, b) => {
    if (sortConfig.key === 'title') {
      return sortConfig.direction === 'ascending' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
    } else {
      const aScore = a.traitScores[sortConfig.key] || 0;
      const bScore = b.traitScores[sortConfig.key] || 0;
      return sortConfig.direction === 'ascending' ? aScore - bScore : bScore - aScore;
    }
  }) : filteredCareers;
  const handleSort = (key: string) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({
      key,
      direction
    });
  };
  const handleTraitToggle = (trait: string) => {
    if (selectedTraits.includes(trait)) {
      setSelectedTraits(selectedTraits.filter(t => t !== trait));
    } else {
      setSelectedTraits([...selectedTraits, trait]);
    }
  };
  const displayTraits = selectedTraits.length > 0 ? selectedTraits : traits;
  return <div data-id="element-157">
      <div className="mb-6" data-id="element-158">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-159">
          Career Genome Matrix
        </h1>
        <p className="text-gray-600" data-id="element-160">
          Edit trait scores for each career role to fine-tune recommendation
          algorithms
        </p>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-161">
        <div className="p-6 border-b border-gray-200" data-id="element-162">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0" data-id="element-163">
            <div className="flex items-center space-x-4" data-id="element-164">
              <div className="relative" data-id="element-165">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-166">
                  <SearchIcon size={16} className="text-gray-400" data-id="element-167" />
                </div>
                <input type="text" placeholder="Search careers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7D56F4] focus:border-[#7D56F4] w-full md:w-64" data-id="element-168" />
              </div>
              <div className="relative inline-block text-left" data-id="element-169">
                <button type="button" className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7D56F4]" id="traits-menu" aria-expanded="true" aria-haspopup="true" data-id="element-170">
                  Filter Traits
                  <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-171">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-172" />
                  </svg>
                </button>
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none z-10" data-id="element-173">
                  <div className="py-1" role="none" data-id="element-174">
                    {traits.map(trait => <div key={trait} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-175">
                        <input type="checkbox" id={`trait-${trait}`} checked={selectedTraits.length === 0 || selectedTraits.includes(trait)} onChange={() => handleTraitToggle(trait)} className="h-4 w-4 text-[#7D56F4] focus:ring-[#7D56F4] border-gray-300 rounded" data-id="element-176" />
                        <label htmlFor={`trait-${trait}`} className="ml-2 block text-sm text-gray-900" data-id="element-177">
                          {trait}
                        </label>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex space-x-2" data-id="element-178">
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7D56F4]" data-id="element-179">
                <UploadIcon size={16} className="mr-2" data-id="element-180" />
                Import CSV
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7D56F4]" data-id="element-181">
                <DownloadIcon size={16} className="mr-2" data-id="element-182" />
                Export CSV
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-183">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-184">
            <thead className="bg-gray-50" data-id="element-185">
              <tr data-id="element-186">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('title')} data-id="element-187">
                  <div className="flex items-center" data-id="element-188">
                    Career Title
                    {sortConfig?.key === 'title' && <span className="ml-1" data-id="element-189">
                        {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                      </span>}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-190">
                  Category
                </th>
                {displayTraits.map(trait => <th key={trait} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort(trait)} data-id="element-191">
                    <div className="flex items-center" data-id="element-192">
                      {trait}
                      {sortConfig?.key === trait && <span className="ml-1" data-id="element-193">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-194">
              {sortedCareers.map(career => <tr key={career.id} data-id="element-195">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-196">
                    <div className="text-sm font-medium text-gray-900" data-id="element-197">
                      {career.title}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-198">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-199">
                      {career.category}
                    </span>
                  </td>
                  {displayTraits.map(trait => <td key={`${career.id}-${trait}`} className="px-6 py-4 whitespace-nowrap" data-id="element-200">
                      <div className="flex items-center" data-id="element-201">
                        <input type="number" value={career.traitScores[trait] || 0} min="0" max="100" className="w-16 py-1 px-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7D56F4] focus:border-[#7D56F4]" data-id="element-202" />
                        <div className="ml-2 w-24 bg-gray-200 rounded-full h-2.5" data-id="element-203">
                          <div className="bg-[#7D56F4] h-2.5 rounded-full" style={{
                      width: `${career.traitScores[trait] || 0}%`
                    }} data-id="element-204"></div>
                        </div>
                      </div>
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-gray-200" data-id="element-205">
          <div className="flex justify-between items-center" data-id="element-206">
            <div className="flex items-center text-sm text-gray-700" data-id="element-207">
              <InfoIcon size={16} className="text-gray-400 mr-1" data-id="element-208" />
              <span data-id="element-209">Changes are automatically saved</span>
            </div>
            <button className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#7D56F4] hover:bg-[#6A46E5]" data-id="element-210">
              Apply Changes
            </button>
          </div>
        </div>
      </div>
    </div>;
};